import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing fullscreen functionality
 * @param {Object} options - Configuration options
 * @param {React.RefObject} options.elementRef - Optional ref to the element to make fullscreen (if not provided, creates one)
 * @returns {Object} Fullscreen utilities and state
 */
const useFullscreen = (options = {}) => {
  const { elementRef: externalElementRef } = options;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const internalElementRef = useRef(null);
  
  // Use external ref if provided, otherwise use internal ref
  const elementRef = externalElementRef || internalElementRef;

  // Check if fullscreen is supported
  const isFullscreenSupported = !!(
    document.fullscreenEnabled || 
    document.webkitFullscreenEnabled || 
    document.mozFullScreenEnabled || 
    document.msFullscreenEnabled
  );

  // Handle fullscreen change events
  const handleFullscreenChange = useCallback(() => {
    const isCurrentlyFullscreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
    setIsFullscreen(isCurrentlyFullscreen);
  }, []);

  // Toggle fullscreen mode
  const toggleFullscreen = useCallback(async () => {
    const element = elementRef.current;
    if (!element) return;

    try {
      if (!document.fullscreenElement &&
          !document.webkitFullscreenElement &&
          !document.mozFullScreenElement &&
          !document.msFullscreenElement) {
        // Enter fullscreen
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await element.webkitRequestFullscreen();
        } else if (element.mozRequestFullScreen) {
          await element.mozRequestFullScreen();
        } else if (element.msRequestFullscreen) {
          await element.msRequestFullscreen();
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      }
    } catch (error) {
      // Fullscreen operation failed silently
    }
  }, [elementRef]);

  // Enter fullscreen programmatically
  const enterFullscreen = useCallback(async () => {
    if (!isFullscreen) {
      await toggleFullscreen();
    }
  }, [isFullscreen, toggleFullscreen]);

  // Exit fullscreen programmatically
  const exitFullscreen = useCallback(async () => {
    if (isFullscreen) {
      await toggleFullscreen();
    }
  }, [isFullscreen, toggleFullscreen]);

  // Set up event listeners
  useEffect(() => {
    const events = [
      'fullscreenchange',
      'webkitfullscreenchange', 
      'mozfullscreenchange',
      'MSFullscreenChange'
    ];

    events.forEach(event => {
      document.addEventListener(event, handleFullscreenChange);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleFullscreenChange);
      });
    };
  }, [handleFullscreenChange]);

  return {
    isFullscreen,
    isFullscreenSupported,
    toggleFullscreen,
    enterFullscreen,
    exitFullscreen,
    elementRef: internalElementRef, // Return internal ref for components that need it
  };
};

export default useFullscreen; 